import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HANDLED_ERROR_STATUSES, TABLE_DEFAULT_HANDLED_STATUSES } from 'src/app/common/table/table-resource';
import { QueuesResponseDto, QueueUpdatesResponseDto } from './queue-request.model';
import { dayBounds } from './queue-request.util';

/**
 * Everything above the time and status buttons in the UI — the filters that change what is being
 * counted. Shared verbatim by the rows call and the counts call, so the two cannot diverge.
 */
export interface QueueCountsQuery {
    departmentId: string | null;
    customerId: number | null;
    synchronizationId: string | null;
    templateId: string | null;
    search: string;
    /** 'yyyy-MM-dd', local — converted to UTC boundaries on the way out. */
    day: string | null;
}

/** The counts query plus the two controls that only narrow the rows. */
export interface QueueServerQuery extends QueueCountsQuery {
    window: string;
    statusGroup: string;
}

const BASE = `${environment.adminConfig.apiUri}/synchronizationmonitoring`;

@Injectable({ providedIn: 'root' })
export class QueueRequestService {
    private readonly http = inject(HttpClient);

    /**
     * Every call opts out of the global error toast: the browser renders its failures inline, and a
     * polling endpoint failing would otherwise raise a toast every ten seconds forever.
     */
    private readonly context = new HttpContext().set(HANDLED_ERROR_STATUSES, TABLE_DEFAULT_HANDLED_STATUSES);

    scopeUrl(departmentId: string, synchronizationId: string | null): string {
        const params = synchronizationId ? `?synchronizationId=${encodeURIComponent(synchronizationId)}` : '';
        return `${BASE}/scope/${departmentId}${params}`;
    }

    templatesUrl(departmentId: string): string {
        return `${BASE}/templates/${departmentId}`;
    }

    detailUrl(queueRequestId: string): string {
        return `${BASE}/queues/${queueRequestId}`;
    }

    /**
     * The badge numbers. Note the absence of `window` and `statusGroup` — the response covers every
     * combination, so those two controls re-read it rather than re-requesting it.
     */
    countsUrl(query: QueueCountsQuery): string {
        return `${BASE}/queues/counts?${this.countsParams(query)}`;
    }

    private payloadUrl(queueRequestId: string, executerId: string): string {
        return `${BASE}/queues/${queueRequestId}/executers/${executerId}/payload`;
    }

    /** Shared by the resources so they all carry the same credentials + error contract. */
    get requestOptions(): { withCredentials: true; context: HttpContext } {
        return { withCredentials: true, context: this.context };
    }

    getQueues(
        query: QueueServerQuery,
        options: { take: number; beforeTimestamp?: string; anchorQueueRequestId?: string },
        abortSignal?: AbortSignal
    ): Promise<QueuesResponseDto> {
        let params = this.baseParams(query)
            .set('statusGroup', query.statusGroup)
            .set('take', String(options.take));

        if (options.beforeTimestamp) params = params.set('beforeTimestamp', options.beforeTimestamp);
        if (options.anchorQueueRequestId) params = params.set('anchorQueueRequestId', options.anchorQueueRequestId);

        return this.send<QueuesResponseDto>(`${BASE}/queues`, params, abortSignal);
    }

    getQueueUpdates(
        query: QueueServerQuery,
        afterTimestamp: string,
        abortSignal?: AbortSignal
    ): Promise<QueueUpdatesResponseDto> {
        // No statusGroup on purpose: a row that has left the active group must still be reported so
        // the client can drop it, or it shows its old status forever.
        const params = this.baseParams(query).set('afterTimestamp', afterTimestamp);
        return this.send<QueueUpdatesResponseDto>(`${BASE}/queues/updates`, params, abortSignal);
    }

    /**
     * Fetch the payload and hand it to the browser as a download.
     *
     * Appends the anchor before clicking and revokes the object URL on the next tick — Firefox
     * ignores a click on a detached anchor, and revoking synchronously cancels the download.
     */
    async downloadPayload(queueRequestId: string, executerId: string, fileName: string): Promise<void> {
        const blob = await firstValueFrom(
            this.http.get(this.payloadUrl(queueRequestId, executerId), {
                ...this.requestOptions,
                responseType: 'blob'
            })
        );

        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }

    private countsParams(query: QueueCountsQuery): HttpParams {
        let params = new HttpParams();

        if (query.synchronizationId) params = params.set('synchronizationId', query.synchronizationId);
        if (query.customerId !== null) params = params.set('customerId', String(query.customerId));
        if (query.departmentId) params = params.set('departmentId', query.departmentId);
        if (query.templateId) params = params.set('templateId', query.templateId);
        if (query.search.trim()) params = params.set('search', query.search.trim());

        // Sent whenever a date is picked, even on a rolling window, so the day badge keeps a number.
        if (query.day) {
            const bounds = dayBounds(query.day);
            if (bounds) params = params.set('dayFrom', bounds.from).set('dayTo', bounds.to);
        }

        return params;
    }

    private baseParams(query: QueueServerQuery): HttpParams {
        return this.countsParams(query).set('window', query.window);
    }

    private send<T>(url: string, params: HttpParams, abortSignal?: AbortSignal): Promise<T> {
        const request = firstValueFrom(this.http.get<T>(url, { ...this.requestOptions, params }));
        if (!abortSignal) return request;

        // resource() aborts a superseded load; without this the stale response still resolves and
        // the store has to lean entirely on its key stamp to reject it.
        return Promise.race([
            request,
            new Promise<never>((_, reject) => {
                abortSignal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), {
                    once: true
                });
            })
        ]);
    }
}
